/**
 * Which external label provider this screen is configuring.
 *
 * The screen was built for Microsoft only. Google Drive labeling reuses almost
 * all of it: the same Cognni category rail, the same mapping rows, and the same
 * integrations-manager endpoints (/label-configs, /label-mappings,
 * /label-overrides). Two things differ, and both live here:
 *
 * 1. Where the provider's own label list comes from. Microsoft labels come from
 *    /mip/labels/getLabels; Google labels come from google-label-webapi via
 *    /google/list-labels and are normalised to MipLabel shape.
 * 2. The `externalLabelProvider` dimension. Every integrations-manager call
 *    that reads or writes provider-specific data must carry it, or the request
 *    silently falls through to Microsoft's records.
 *
 * Note the integrations-manager payloads keep the `mipLabelId` /
 * `mipLabelName` / `mipLabelFullName` field names for Google too: the Google
 * label's name goes in the `mipLabelName` fields and its id in `mipLabelId`.
 * The Microsoft-flavoured naming is legacy and deliberately kept.
 */
export type LabelProvider = "microsoft" | "google";

export interface LabelProviderConfig {
  id: LabelProvider;
  /** page heading */
  title: string;
  subtitle: string;
  /** the provider's own labels, as shown in the right-hand column */
  labels_column: string;
  /** short form for inline phrases ("Configure your X Labeling rules") */
  short_name: string;
  /** the provider's label set, as titled in the APPLY column */
  labels_set_name: string;
  /** brand mark id in components/root/icons/SourceIcons.vue */
  icon: string;
  /** endpoint serving this provider's label list */
  labels_endpoint: string;
  /**
   * The provider dimension integrations-manager expects. Microsoft is the
   * implicit default and sends nothing; omitting it for Google reads and
   * writes Microsoft's mappings instead.
   */
  external_label_provider?: string;
  /**
   * The provider's key in label-api's normalized beta endpoints (task 25038):
   * the object a mapping or override is written under, and the `application`
   * of a delete. Only Microsoft's ("mip") is specified; a provider without one
   * keeps writing through integrations-manager.
   */
  label_api_application?: string;
}

export const LABEL_PROVIDERS: Record<LabelProvider, LabelProviderConfig> = {
  microsoft: {
    id: "microsoft",
    title: "Mapping Information Types to Microsoft Sensitivity Labels",
    subtitle:
      "Manage your linked <b>Cognni categories</b> &harr; <b>Microsoft labels</b>",
    labels_column: "Microsoft Label",
    short_name: "Microsoft",
    labels_set_name: "Microsoft Sensitivity Labels",
    icon: "Ms",
    labels_endpoint:
      "/mip/labels/getLabels?cid=ca9befe5-f089-498a-8e8d-8f10f0bfd404",
    label_api_application: "mip",
  },
  google: {
    id: "google",
    title: "Mapping Information Types to Google Drive Labels",
    subtitle:
      "Manage your linked <b>Cognni categories</b> &harr; <b>Google Drive labels</b>",
    labels_column: "Google Drive Label",
    short_name: "Google Drive",
    labels_set_name: "Google Drive Labels",
    icon: "Gdrive",
    labels_endpoint: "/google/list-labels",
    external_label_provider: "google-labels",
  },
};

/** the screen defaults to Microsoft, so every existing entry point is unchanged */
export const DEFAULT_PROVIDER: LabelProvider = "microsoft";

export const provider_config = (p?: LabelProvider) =>
  LABEL_PROVIDERS[p || DEFAULT_PROVIDER];

/** the provider's label-api beta key, or undefined to keep the legacy writes */
export const label_api_application_of = (p?: LabelProvider) =>
  provider_config(p).label_api_application;

/** `undefined` for Microsoft, so its payloads stay byte-identical to before */
export const external_provider_of = (p?: LabelProvider) =>
  provider_config(p).external_label_provider;
